// London's page: the shell (@tol/ui) fed with TfL's route sequences (the
// network, rebuilt as routes arrive), TfL's arrival predictions (the trains,
// through tfl-trains.ts) and line status, plus National Rail (nr-ui.ts).

import * as Feed from '@tol/core/feed';
import type { Network, RouteResponse } from '@tol/core/types';
import { el } from '@tol/ui/dom';
import { createShell } from '@tol/ui/shell';
import { STATUS_MS, parseLondonStatus, londonStatus, londonIdle } from './status.ts';
import type { LondonStatuses } from './status.ts';
import { ARRIVAL_GROUPS, LINES, tflIds } from './lines.ts';
import { NR_CREDIT } from './nr.ts';
import { createNrUi } from './nr-ui.ts';
import { TFL_FEED } from './tfl-trains.ts';
import { supplemental, mergeNetworks } from './supplemental.ts';
import { gtfsTripRows, gtfsTrainNote } from '@tol/gtfs/trip-rows';
import { lineStatus } from '@tol/gtfs/page';
import { londonNetwork, londonTripRows, withPageLines } from './trips.ts';
import { EUROSTAR_CREDIT } from '../shared/eurostar.ts';
import { createElizabethHandover } from './elizabeth-handover.ts';

// Relative, so the page works at a domain's root and under a path.
const API = 'tfl';
const POLL_MS = 20000;
const VERSION = new URL(import.meta.url).search;

// TfL's API, through the server's proxy.
const client = Feed.createClient({ base: API });
const getJSON = (path: string, signal?: AbortSignal): Promise<unknown> => client.getJSON(path, signal);

function hasStatus(error: unknown, status: number): boolean {
  return typeof error === 'object' && error !== null && 'status' in error && error.status === status;
}

// ---------------------------------------------------------------- network

// Route sequences for every line and direction. Whatever fails, at startup
// or later, is retried with backoff, and the network is rebuilt as routes
// arrive (see Feed.keepRoutes). The page starts once the first usable
// network exists, even if that is after a total outage. Routes load while
// the map's style does.
const routes = Feed.createRouteLoader({
  lineIds: tflIds(LINES.map((l) => l.id)),
  fetchRoute: (lineId, direction) => getJSON(`/Line/${lineId}/Route/Sequence/${direction}`),
});
// Track shapes (built from OpenStreetMap by scripts/build-geometry.ts).
// Optional: without the file, lines are straight hops between stations.
async function loadGeometry(): Promise<unknown> {
  try {
    const r = await fetch('geometry.json' + VERSION);
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}
const network = Feed.keepRoutes({
  loader: routes,
  onProgress: (done, total) => {
    el('#loader-bar').style.width = `${(done / total) * 100}%`;
    el('#loader-text').textContent =
      `Drawing the network: ${Math.ceil(done / 2)} of ${LINES.length} TfL lines, then National Rail`;
  },
  onChange: networkChanged,
  onFailure: ({ failed, error }, loaded) => {
    console.warn(`${failed.length} route requests failed; retrying`, failed);
    if (loaded !== 0) return;
    el('#loader-text').innerHTML = hasStatus(error, 429)
      ? 'TfL is rate-limiting requests. Retrying automatically; adding a TfL app key to the server avoids this.'
      : 'Couldn\u2019t load TfL data. Retrying automatically. This page needs its server to talk to TfL, so run <code>pnpm run serve</code> locally or open the deployed site.';
    el('.live-dot').classList.add('stale');
  },
});
void network.start();

// ---------------------------------------------------------------- the shell

// A line's worst current status, and all of them for its detail.
let status: LondonStatuses = {};

let extra: ReturnType<typeof supplemental> | null = null;
let nr: ReturnType<typeof createNrUi> | null = null;
const parts = new Map<string, Network>();
const shellPromise = createShell({
  hooks: 'tubeLive',
  city: 'london',
  lines: LINES,
  // Central London; wide enough for National Rail: Oxford and Swindon to
  // Dover, Brighton and Southampton to Cambridge and Peterborough.
  view: {
    bounds: {
      phone: [
        [-0.15, 51.505],
        [-0.085, 51.52],
      ],
      desktop: [
        [-0.165, 51.497],
        [-0.075, 51.53],
      ],
    },
    minZoom: 5,
    maxZoom: 17,
    maxBounds: [
      [-6.5, 45.2],
      [8, 59],
    ],
  },
  credits: [
    'Bus GPS: <a href="https://data.bus-data.dft.gov.uk/">BODS</a> (OGL v3)',
    'Powered by <a href="https://tfl.gov.uk/info-for/open-data-users/">TfL Open Data</a>',
    NR_CREDIT,
    EUROSTAR_CREDIT,
  ],
  timeZone: 'Europe/London',
  locale: 'en-GB',
  dataName: "TfL's newest predictions",
  noun: { one: 'vehicle', many: 'vehicles' },
  staleAfterS: 180,
  client,
  // Handy in the console: National Rail trains drawn (matched, on a line we show).
  extraHooks: { nrTrains: () => [...(nr?.extras.counts?.()?.values() ?? [])].reduce((a, b) => a + b, 0) },
  card: {
    tripRows: (t, net) => {
      const c = extra?.clients.get(t.pred.lineId.startsWith('bus:') ? 'bus' : 'rail');
      const trip = c?.trip(t.pred.key);
      return trip ? gtfsTripRows(t, net, trip, c?.file()?.shapes ?? {}) : londonTripRows(t, net, shell.now());
    },
    trainNote: (t) => {
      const c = extra?.clients.get(t.pred.lineId.startsWith('bus:') ? 'bus' : 'rail');
      const trip = c?.trip(t.pred.key);
      return trip === undefined
        ? ''
        : (t.pred.lineId.startsWith('bus:') ? 'Bus ' + (trip.vid ?? '') : gtfsTrainNote(t, trip)) +
            (trip.lat === undefined ? ' · prediction-derived position' : ' · GPS');
    },
    stationSection: (sid) => nr?.stationSection(sid) ?? null,
  },
  status: (id) =>
    id.startsWith('bus:') || id === 'eurostar'
      ? lineStatus(extra?.clients.get(id === 'eurostar' ? 'rail' : 'bus')?.alert(id), {
          covered: extra?.clients.get(id === 'eurostar' ? 'rail' : 'bus')?.covered(id) ?? false,
          clock: (ms) => new Date(ms).toLocaleTimeString('en-US'),
        })
      : londonStatus(status, id),
  idle: (id) => londonIdle(status, id),
  onHidden: () => {
    poller.cancel();
    extra?.stop();
  },
  onVisible: () => {
    void poller.poll();
    extra?.start();
  },
  // National Rail's, once it exists (below): the shell calls these later.
  extras: {
    draw: (ctx, dt, scale, w, h) => nr?.extras.draw?.(ctx, dt, scale, w, h),
    drawn: () => nr?.extras.drawn?.() ?? [],
    pick: (pt, r) => nr?.extras.pick?.(pt, r) ?? null,
    stationAt: (pt, radius) => nr?.extras.stationAt?.(pt, radius) ?? null,
    renderCard: (card, sel, full) => nr?.extras.renderCard?.(card, sel, full) ?? false,
    rosterRows: () => nr?.extras.rosterRows?.() ?? [],
    counts: (within) => nr?.extras.counts?.(within) ?? null,
    onStationsVisible: (v) => nr?.extras.onStationsVisible?.(v),
    followed: (sel) => nr?.extras.followed?.(sel) ?? null,
  },
});
// The map and the track shapes load together; the routes (above) meanwhile.
const ready = Promise.all([shellPromise, loadGeometry()]);
const [shell] = await ready;
const elizabeth = createElizabethHandover(shell.live, '', shell.now);
nr = createNrUi(shell, {
  showElizabeth: elizabeth.allowsTd,
  onDraw: () => {
    if (elizabeth.afterTflDraw()) shell.updateArrivalUI();
  },
  onMappedUpdate: () => {
    elizabeth.reconcile();
    extra?.reconcileRail();
    shell.updateArrivalUI();
  },
});
elizabeth.setOwner(nr.ownsElizabeth);
nr.extras.onMapReady?.(shell);
extra = supplemental(shell, nr.hasUid, (key, net) => {
  parts.set(key, net);
  shell.setNetwork(mergeNetworks([...parts.values()]));
});
extra.start();

// Build the network from every route so far and redraw everything drawn
// from it. The first time, also start the live map.
let started = false;
async function networkChanged(responses: readonly RouteResponse[]): Promise<void> {
  const [s, geometry] = await ready;
  const net = londonNetwork(responses, s.isGeometryFile(geometry) ? geometry : null);
  parts.set('tfl', net);
  s.setNetwork(mergeNetworks([...parts.values()]));
  if ((net.geom?.size ?? 0) > 0 && !s.map.getSource('osm-track-credit')) {
    // MapLibre collects attributions from sources; a source with no data
    // carries the track credit.
    s.map.addSource('osm-track-credit', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      attribution: 'Track &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    });
    s.map.addLayer({ id: 'osm-track-credit', type: 'circle', source: 'osm-track-credit' });
  }
  if (!started) {
    started = true;
    void startLive();
  }
}

// ---------------------------------------------------------------- polling

let onFirstTrains: (() => void) | null = null;
function updateArrivalUI(): void {
  elizabeth.reconcile();
  if (onFirstTrains) {
    onFirstTrains();
    onFirstTrains = null;
  }
  shell.updateArrivalUI();
}

// The poller only runs once the network exists: startLive starts it from
// the first networkChanged, and visibilitychange only polls with a network.
function loadedNetwork(): Network {
  if (!shell.state.net) throw new Error('Polled before the network loaded');
  return shell.state.net;
}

const poller = Feed.createPoller({
  store: shell.live,
  core: {
    ...TFL_FEED,
    computeTrains: (net, predictions, now) => {
      elizabeth.restore();
      return TFL_FEED.computeTrains(net, predictions, now);
    },
  },
  groups: ARRIVAL_GROUPS,
  // TfL's lines, then each prediction on the page's line (the Northern line's
  // trains via Charing Cross are the Charing Cross line's).
  fetchGroup: async (group, signal) => {
    const got = await client.getSampledJSON('/Line/' + tflIds(group).join(',') + '/Arrivals', signal);
    if (!Array.isArray(got.data)) return got;
    return { ...got, data: withPageLines(got.data) };
  },
  net: loadedNetwork,
  now: shell.now,
  onUpdate: updateArrivalUI,
  onSettled: ({ failed, error }) => {
    if (error !== null && error !== undefined && error !== false && error !== 0 && error !== '')
      shell.showToast('Train updates are unavailable. Retrying shortly.');
    else if (failed.length > 0)
      shell.showToast('Some train updates are unavailable. Showing the last known positions and retrying.');
    else shell.hideToast();
  },
  isHidden: () => document.hidden,
  pollMs: POLL_MS,
});

async function pollStatus(): Promise<void> {
  try {
    const data = await getJSON(`/Line/${tflIds(LINES.map((l) => l.id)).join(',')}/Status`);
    status = parseLondonStatus(data);
    shell.renderRoster();
  } catch (e) {
    status = {};
    shell.renderRoster();
    console.warn('status failed', e);
  }
  setTimeout(() => void pollStatus(), STATUS_MS);
}

// ---------------------------------------------------------------- boot

async function startLive(): Promise<void> {
  // Open the map as soon as the first line group has trains. A slow TfL
  // response for another group shouldn't hold everything behind the loader.
  const firstTrains = new Promise<void>((resolve) => {
    onFirstTrains = resolve;
  });
  const allTrains = poller.poll();
  await Promise.race([firstTrains, allTrains]);
  shell.ready();
  void allTrains.then(pollStatus);
}
